import React, { createContext, useContext } from 'react';
import { useAuthState } from '../hooks/useAuthState';
import type { AccessTokenPayload } from '@/api/auth/interfaces';

interface AuthContextProps {
  /** True when a valid, non-expired access token is present. */
  isAuthenticated: boolean;
  /** Decoded JWT payload for the logged-in user, or null when logged out. */
  user: AccessTokenPayload | null;
  /** True while the initial token read/decode is in progress. */
  loading: boolean;
  /** Re-read the access token cookie and refresh context (e.g. after login or tenant switch). */
  reload: () => void;
  /** Remove the access token and reload the page. */
  clearAuth: () => void;
}

const defaultState: AuthContextProps = {
  isAuthenticated: false,
  user: null,
  loading: true,
  reload: () => {},
  clearAuth: () => {}
};

const AuthContext = createContext<AuthContextProps>(defaultState);

/**
 * Read the shared auth state from context.
 *
 * Prefer {@link useAuth} in most code — it exposes the same data with a
 * `payload` alias that matches existing call sites. Use this hook directly
 * when you specifically need the `user` field name.
 */
export const useAuthContext = () => useContext(AuthContext);

/**
 * Provides a single, app-wide auth state to all descendants.
 *
 * Wraps the tree near the root (see `App.tsx`) and delegates token
 * management to {@link useAuthState}. Every component that calls
 * `useAuth()` or `useAuthContext()` reads from this provider instead
 * of maintaining its own copy of the session.
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { payload, loading, reload, clearAuth, isAuthenticated } = useAuthState();

  const value = {
    isAuthenticated,
    user: payload,
    loading,
    reload,
    clearAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
